import React, { useEffect, useRef, useState } from 'react'
import { Box, Card, Typography } from '@mui/material'
import Breadcrumbs from '../../../components/breadcrumbs'
import { useDispatch, useSelector } from 'react-redux'
import KioskHeader from '../../../components/kiosk/dashboard/headerCard'
import KioskTable, { TABLE_HEAD } from '../../../components/kiosk/dashboard/table'
import { getKioskBookingDetailsWatcher, getNotificationDetailsWatcher } from '../../../redux/actionCreators/kiosk'
// import { updateAlert } from '../../../redux/actionCreators/alert'
import CustomAlert from '../../../components/CustomAlert'

/*
  This screen used to hold a WebSocket open and refresh when the backend pushed a
  'newOrder' event. That socket has been removed - it accepted any connection with
  no authentication and broadcast every order to all of them, which was the last
  unauthenticated way into the backend.

  The order itself was never carried by the socket: the backend writes it through
  addKioskNotificationsModel either way, and the socket only said "something
  changed, go and look". Polling says the same thing a few seconds later, needs no
  server-side component, and goes through the authenticated axios instance like
  every other request.

  Both dispatches pass noLoader so the refresh never puts the full-screen spinner
  over a dashboard somebody is reading.
*/
const POLL_INTERVAL_MS = 15000;

export default function Kiosk() {

    const { KioskRoleReducers: { kioskDashboardList, notificationDetails }, ProfileReducers: { loginState } } = useSelector((state) => state)
    const cEmployeeName = loginState[0]?.cEmployeeName || ''
    const dispatch = useDispatch()
    const [searchText, setSearchText] = useState('')
    const [searchVal, setSearchVal] = useState([])
    const [page, setPage] = useState(1);
    const [open, setOpen] = useState(false)
    const [message, setMessage] = useState({})

    // Highest notification id already accounted for. null until the first list
    // arrives, so opening the screen does not announce orders that are already there.
    const seenNotificationId = useRef(null)

    function escapeRegExp(value) {
        return value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
    }

    const handleSearch = (e) => {
        let val = e.target.value
        setSearchText(val);
        const searchRegex = new RegExp(escapeRegExp(val), 'i');
        const filteredRows = kioskDashboardList.filter((row) => {
            return Object.keys(row).some((field) => {
                return searchRegex.test(row[field]);
            });
        });

        setSearchVal(filteredRows);
        setPage(1)
    }

    useEffect(() => {
        const refresh = () => {
            dispatch(getKioskBookingDetailsWatcher({ "ACTION": "getKioskBookingDetails" }, true))
            dispatch(getNotificationDetailsWatcher({ "ACTION": "Select" }, true))
        }

        // A kiosk screen is left open all day. Polling a hidden tab is just load
        // on the server for a dashboard nobody is looking at; the visibility
        // handler catches it up the moment it comes back.
        const tick = () => {
            if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return
            refresh()
        }

        refresh()
        const timer = setInterval(tick, POLL_INTERVAL_MS)

        const onVisible = () => {
            if (document.visibilityState === 'visible') refresh()
        }
        document.addEventListener('visibilitychange', onVisible)

        return () => {
            clearInterval(timer)
            document.removeEventListener('visibilitychange', onVisible)
        }
    }, [dispatch])

    /*
      Announce genuinely new orders.

      Tracks the highest nID seen rather than the list's first element, because
      nothing here guarantees the order the stored procedure returns rows in.
    */
    useEffect(() => {
        if (!notificationDetails || !notificationDetails.length) return

        const newest = notificationDetails.reduce(
            (top, n) => (Number(n.nID) > Number(top.nID) ? n : top),
            notificationDetails[0]
        )
        const newestId = Number(newest.nID)

        if (seenNotificationId.current === null) {
            seenNotificationId.current = newestId
            return
        }

        if (newestId > seenNotificationId.current) {
            seenNotificationId.current = newestId
            setMessage({ title: newest.title, body: newest.body })
            setOpen(true)
        }
    }, [notificationDetails])

    return (
        <>
            <Box p="24px">
                <Breadcrumbs content={["Dashboard"]} />
                <Box mt="14px" mb='8px' display='flex' alignItems='center' >
                    <Typography variant='t24_700' > {`Welcome, ${cEmployeeName}`} </Typography>
                </Box>
                <Box p='10px 0'>
                    <KioskHeader handleSearch={handleSearch} searchText={searchText} rowData={kioskDashboardList} columnData={TABLE_HEAD} />
                </Box>
                <Card style={{ boxShadow: (theme) => `0px 12px 23px ${theme.palette.grey[200]}`, borderRadius: "6px 6px 0px 0px", padding: '20px' }}>
                    <KioskTable page={page} setPage={setPage} searchVal={searchVal} searchText={searchText} />
                </Card>
                <CustomAlert open={open} setOpen={setOpen} message={message} />
            </Box>
        </>
    )
}
